import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TrustDTO, TrustsDTO, useTrustsQuery } from '../../api/useTrustsQuery';
import { TruncatedText } from '../../../../CommonComponents/OPL/Text/TruncatedText';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { DeleteTrustModal } from './components/DeleteTrustModal/DeleteTrustModal';
import { Actions, TrustItem, TrustsList } from './Trusts.styles';
import { UpsertTrustModal } from './components/UpsertTrustModal/UpsertTrustModal';
import { SectionHeading } from '../../PersonalInfo.styles';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { PersonalInfoDTO } from '../../api/usePersonalInfoQuery';

export interface Props {
  data?: TrustsDTO;
  submitPersonalInfo(): void;
  resetPersonalInfo(data: PersonalInfoDTO): void;
  isEstatePlanning?: boolean;
}

export const Trusts: React.FC<Props> = ({
  data,
  submitPersonalInfo,
  resetPersonalInfo,
  isEstatePlanning,
}) => {
  if (!data) {
    return null;
  }

  const { Items: trusts } = data;

  const [trustToUpsert, setTrustToUpsert] = useState<Partial<TrustDTO> | null>(null);

  const [trustToDelete, setTrustToDelete] = useState<{ Id: string } | null>(null);

  const [isOpeningUpsertModal, setIsOpeningUpsertModal] = useState(false);

  const { trigger } = useFormContext();

  const { refetch } = useTrustsQuery();

  const onOpenTrustsModal = async (trustId?: string) => {
    setIsOpeningUpsertModal(true);

    const isPersonalInfoFormValid = await trigger();

    await submitPersonalInfo();

    if (isPersonalInfoFormValid) {
      const { data: refetchData } = await refetch();

      if (trustId) {
        const foundTrust = refetchData?.Items.find(trust => trust.Id === trustId);

        if (foundTrust) {
          setTrustToUpsert(foundTrust);
        } else {
          throw new Error(`Trust with id ${trustId} not found`);
        }
      } else {
        setTrustToUpsert({});
      }
    }

    setIsOpeningUpsertModal(false);
  };

  return (
    <>
      {isOpeningUpsertModal && <FullPageLoading />}

      <section>
        <SectionHeading as="h2">Trusts</SectionHeading>

        {trusts.length > 0 && (
          <TrustsList as="ul">
            {trusts.map(trust => (
              <TrustItem as="li" key={trust.Id}>
                <TruncatedText fontWeight={500} textAlign="left" width="100%">
                  {trust.Name}
                </TruncatedText>

                <Actions>
                  <IconButton
                    icon="far fa-pencil"
                    variant="subtleInactive"
                    onClick={() => onOpenTrustsModal(trust.Id)}
                    aria-label="Edit trust"
                  />

                  <IconButton
                    icon="far fa-minus-circle"
                    variant="subtleInactive"
                    onClick={() => setTrustToDelete({ Id: trust.Id })}
                    aria-label="Delete trust"
                  />
                </Actions>
              </TrustItem>
            ))}
          </TrustsList>
        )}

        <Button
          leftIcon="fas fa-plus"
          variant="link"
          onClick={() => onOpenTrustsModal()}
          mt={trusts.length > 0 ? '32' : '0'}
        >
          Add Trust
        </Button>
      </section>

      {trustToUpsert && (
        <UpsertTrustModal
          trust={trustToUpsert}
          data={data}
          resetPersonalInfo={resetPersonalInfo}
          onClose={() => setTrustToUpsert(null)}
          isEstatePlanning={isEstatePlanning}
        />
      )}

      {trustToDelete && (
        <DeleteTrustModal trustId={trustToDelete.Id} onClose={() => setTrustToDelete(null)} />
      )}
    </>
  );
};
